import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { MessageText, Send } from 'iconoir-react';

interface SupportTicket {
  id: number;
  user: { id: number; name: string; email: string };
  subject: string;
  message: string;
  priority: string;
  status: string;
  category: string;
  created_at: string;
}

const SupportTickets: React.FC = () => {
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState<number | null>(null);
  const [replyMessage, setReplyMessage] = useState('');

  useEffect(() => {
    loadTickets();
  }, []);

  const loadTickets = async () => {
    try {
      const response = await api.get('/admin/support-tickets');
      setTickets(response.data.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateTicketStatus = async (ticketId: number, status: string) => {
    try {
      await api.put(`/admin/support-tickets/${ticketId}`, { status });
      await loadTickets();
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
    }
  };

  const sendReply = async (ticketId: number) => {
    if (!replyMessage.trim()) return;
    try {
      await api.post(`/admin/support-tickets/${ticketId}/messages`, {
        message: replyMessage,
      });
      setReplyMessage('');
      alert('Réponse envoyée avec succès');
    } catch (error) {
      console.error('Erreur lors de l\'envoi:', error);
      alert('Erreur lors de l\'envoi de la réponse');
    }
  };

  const getPriorityBadge = (priority: string) => {
    const badges: { [key: string]: { label: string; color: string } } = {
      low: { label: 'Basse', color: '#95a5a6' },
      medium: { label: 'Moyenne', color: '#3498db' },
      high: { label: 'Haute', color: '#f39c12' },
      urgent: { label: 'Urgente', color: '#e74c3c' },
    };
    return badges[priority] || badges.medium;
  };

  const getStatusBadge = (status: string) => {
    const badges: { [key: string]: { label: string; color: string } } = {
      open: { label: 'Ouvert', color: '#3498db' },
      in_progress: { label: 'En cours', color: '#f39c12' },
      waiting_user: { label: 'En attente utilisateur', color: '#9b59b6' },
      resolved: { label: 'Résolu', color: '#00bf7e' },
      closed: { label: 'Fermé', color: '#95a5a6' },
    };
    return badges[status] || badges.open;
  };

  if (loading) return <div>Chargement...</div>;

  return (
    <div className="support-tickets">
      <div className="section-header">
        <h2>Tickets de support</h2>
        <p>{tickets.length} ticket(s) au total</p>
      </div>

      <div className="tickets-list">
        {tickets.length === 0 ? (
          <div className="empty-state">Aucun ticket de support</div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Utilisateur</th>
                <th>Sujet</th>
                <th>Catégorie</th>
                <th>Priorité</th>
                <th>Statut</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map((ticket) => {
                const priorityBadge = getPriorityBadge(ticket.priority);
                const statusBadge = getStatusBadge(ticket.status);
                return (
                  <tr key={ticket.id}>
                    <td>#{ticket.id}</td>
                    <td>
                      <div>
                        <strong>{ticket.user.name}</strong>
                        <br />
                        <small>{ticket.user.email}</small>
                      </div>
                    </td>
                    <td>
                      <strong>{ticket.subject}</strong>
                      <br />
                      <small>{ticket.message.substring(0, 50)}...</small>
                    </td>
                    <td>{ticket.category}</td>
                    <td>
                      <span
                        className="status-badge"
                        style={{ backgroundColor: priorityBadge.color }}
                      >
                        {priorityBadge.label}
                      </span>
                    </td>
                    <td>
                      <span
                        className="status-badge"
                        style={{ backgroundColor: statusBadge.color }}
                      >
                        {statusBadge.label}
                      </span>
                    </td>
                    <td>{new Date(ticket.created_at).toLocaleDateString('fr-FR')}</td>
                    <td>
                      <div className="action-buttons">
                        {ticket.status === 'open' && (
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => updateTicketStatus(ticket.id, 'in_progress')}
                          >
                            Prendre en charge
                          </button>
                        )}
                        {ticket.status === 'in_progress' && (
                          <button
                            className="btn btn-sm btn-success"
                            onClick={() => updateTicketStatus(ticket.id, 'resolved')}
                          >
                            Résoudre
                          </button>
                        )}
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => setSelectedTicket(ticket.id)}
                        >
                          <MessageText width={16} height={16} strokeWidth={2} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {selectedTicket && (
        <div className="reply-modal">
          <div className="reply-modal-content">
            <h3>Répondre au ticket #{selectedTicket}</h3>
            <textarea
              value={replyMessage}
              onChange={(e) => setReplyMessage(e.target.value)}
              className="form-control"
              rows={5}
              placeholder="Votre réponse..."
            />
            <div className="modal-actions">
              <button
                className="btn btn-primary"
                onClick={() => sendReply(selectedTicket)}
              >
                <Send width={18} height={18} strokeWidth={2} />
                Envoyer
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setSelectedTicket(null)}
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportTickets;
